/// Buys Ballot's Law — visual diagram of the wind direction +
/// pressure-centre geometry. Shows the observer standing with back
/// to the wind, the low pressure on the left (N hemisphere) or right
/// (S hemisphere), and the surrounding isobars + wind arrows.

interface Props {
  hemisphere?: "N" | "S";
  size?: number;
}

const VW = 360;
const VH = 280;
const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const LOW = "#c0382b";
const HIGH = "#1e6091";

export function BuysBallotFigure({ hemisphere = "N", size = VW }: Props) {
  const isN = hemisphere === "N";
  const low = isN
    ? { x: 80, y: 100 }
    : { x: VW - 80, y: 100 };
  const high = isN
    ? { x: VW - 80, y: 200 }
    : { x: 80, y: 200 };
  const observer = { x: VW / 2, y: VH / 2 };

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Buys Ballot — ${hemisphere}orthern hemisphere`}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {/* Background. */}
      <rect
        x={0}
        y={0}
        width={VW}
        height={VH}
        fill="var(--bg-elev, #f8fafc)"
        stroke={MUTED}
      />

      {/* Isobars — concentric circles round each centre. */}
      {[1, 2, 3].map((r) => (
        <g key={`l-${r}`}>
          <circle
            cx={low.x}
            cy={low.y}
            r={r * 20}
            fill="none"
            stroke={MUTED}
            strokeWidth={0.7}
            strokeDasharray="3 3"
          />
        </g>
      ))}
      {[1, 2, 3].map((r) => (
        <g key={`h-${r}`}>
          <circle
            cx={high.x}
            cy={high.y}
            r={r * 22}
            fill="none"
            stroke={MUTED}
            strokeWidth={0.7}
            strokeDasharray="3 3"
          />
        </g>
      ))}

      {/* Pressure centres. */}
      <g>
        <text
          x={low.x}
          y={low.y + 5}
          fontSize={20}
          fontWeight={700}
          textAnchor="middle"
          fill={LOW}
        >
          L
        </text>
        <text
          x={low.x}
          y={low.y - 32}
          fontSize={11}
          fontWeight={600}
          textAnchor="middle"
          fill={LOW}
        >
          LOW
        </text>
      </g>
      <g>
        <text
          x={high.x}
          y={high.y + 5}
          fontSize={20}
          fontWeight={700}
          textAnchor="middle"
          fill={HIGH}
        >
          H
        </text>
        <text
          x={high.x}
          y={high.y - 32}
          fontSize={11}
          fontWeight={600}
          textAnchor="middle"
          fill={HIGH}
        >
          HIGH
        </text>
      </g>

      {/* Wind arrows spiralling INTO the low + OUT of the high.
          Northern hemisphere: anticlockwise into the low. */}
      {/* Sample wind arrows between L and H — at the observer's level
          the wind blows roughly perpendicular to L→H line, with the
          low on the LEFT (N hem). */}
      <WindArrow
        startX={VW / 2 - 60}
        startY={VH / 2 + 20}
        deg={isN ? 90 : 270}
      />
      <WindArrow
        startX={VW / 2 + 0}
        startY={VH / 2 - 10}
        deg={isN ? 90 : 270}
      />
      <WindArrow
        startX={VW / 2 + 60}
        startY={VH / 2 - 30}
        deg={isN ? 80 : 280}
      />

      {/* Observer silhouette — face away from wind, arms out.
          Stick figure pointing in the wind's "FROM" direction (back
          to wind). */}
      <Observer x={observer.x} y={observer.y} hemisphere={hemisphere} />

      {/* "Back to wind" arrow + label */}
      <g>
        <line
          x1={observer.x + (isN ? 40 : -40)}
          y1={observer.y + 50}
          x2={observer.x + (isN ? 90 : -90)}
          y2={observer.y + 50}
          stroke={STROKE}
          strokeWidth={1.2}
        />
        <polygon
          points={
            isN
              ? `${observer.x + 90},${observer.y + 50} ${observer.x + 84},${observer.y + 46} ${observer.x + 84},${observer.y + 54}`
              : `${observer.x - 90},${observer.y + 50} ${observer.x - 84},${observer.y + 46} ${observer.x - 84},${observer.y + 54}`
          }
          fill={STROKE}
        />
        <text
          x={observer.x + (isN ? 65 : -65)}
          y={observer.y + 65}
          fontSize={10}
          textAnchor="middle"
          fill={MUTED}
        >
          wind blows TO
        </text>
      </g>

      {/* Header. */}
      <text x={10} y={20} fontSize={12} fontWeight={700} fill={STROKE}>
        {hemisphere === "N" ? "NORTHERN" : "SOUTHERN"} HEMISPHERE
      </text>
      <text
        x={VW - 10}
        y={VH - 8}
        fontSize={10}
        textAnchor="end"
        fill={MUTED}
      >
        {hemisphere === "N"
          ? "Back to wind → LOW on your LEFT"
          : "Back to wind → LOW on your RIGHT"}
      </text>
    </svg>
  );
}

function WindArrow({
  startX,
  startY,
  deg,
}: {
  startX: number;
  startY: number;
  deg: number;
}) {
  const len = 40;
  const rad = (deg * Math.PI) / 180;
  const ex = startX + Math.cos(rad) * len;
  const ey = startY + Math.sin(rad) * len;
  return (
    <g>
      <line
        x1={startX}
        y1={startY}
        x2={ex}
        y2={ey}
        stroke="#3b78b5"
        strokeWidth={2}
      />
      <polygon
        points={`${ex},${ey} ${ex - 6 * Math.cos(rad - 0.4)},${ey - 6 * Math.sin(rad - 0.4)} ${ex - 6 * Math.cos(rad + 0.4)},${ey - 6 * Math.sin(rad + 0.4)}`}
        fill="#3b78b5"
      />
    </g>
  );
}

function Observer({
  x,
  y,
  hemisphere,
}: {
  x: number;
  y: number;
  hemisphere: "N" | "S";
}) {
  // Stick figure. Back of person is at top — they're facing AWAY
  // from the wind (which blows from behind them).
  const isN = hemisphere === "N";
  return (
    <g>
      {/* Head */}
      <circle cx={x} cy={y - 18} r={6} fill="#facc15" stroke={STROKE} strokeWidth={1} />
      {/* Body */}
      <line x1={x} y1={y - 12} x2={x} y2={y + 8} stroke={STROKE} strokeWidth={2} />
      {/* Arms outstretched — one toward the low. */}
      <line
        x1={x}
        y1={y - 6}
        x2={x + (isN ? -26 : 26)}
        y2={y - 8}
        stroke={STROKE}
        strokeWidth={1.8}
      />
      <text
        x={x + (isN ? -32 : 32)}
        y={y - 12}
        fontSize={9}
        textAnchor={isN ? "end" : "start"}
        fill="#c0382b"
        fontWeight={600}
      >
        ← LOW
      </text>
      <line
        x1={x}
        y1={y - 6}
        x2={x + (isN ? 26 : -26)}
        y2={y - 8}
        stroke={STROKE}
        strokeWidth={1.8}
      />
      {/* Legs */}
      <line x1={x} y1={y + 8} x2={x - 6} y2={y + 24} stroke={STROKE} strokeWidth={2} />
      <line x1={x} y1={y + 8} x2={x + 6} y2={y + 24} stroke={STROKE} strokeWidth={2} />
    </g>
  );
}
